import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import axios from "axios";
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    OutlinedInput,
    Box
} from "@mui/material";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {Header as PageHeader} from "./UserList.styled";
import styled from "styled-components";
import {useSnackbar} from "../../contexts/SnackbarContext";
import {parseError} from "../../services/http.service";
import {useTranslation} from 'react-i18next';
import {validatePassword} from "../../utils/password-validation";
import {media} from '../../theme/breakpoints';
import {StyledContainer} from "../workspace/UserDataSourceList.styled";
import FieldHelpSuffix from "../../components/common/FieldHelpSuffix";

const CreateUserForm = styled.form`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    width: 100%;
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    width: 100%;

    ${media.md} {
        grid-template-columns: 1fr 1fr;
    }
`;

const FullRow = styled.div`
    grid-column: 1 / -1;
    width: 100%;
`;

const ActionsRow = styled(FullRow)`
    display: flex;
    justify-content: flex-end;
`;

const StyledTextField = styled(TextField)`
    width: 100%;
    max-width: none;
`;

const StyledFormControl = styled(FormControl)`
    width: 100%;
    max-width: none;
`;

async function createUser(name: string, surname: string, position: string, phoneNumber: string, isActive: boolean, sendWelcomeEmail: boolean, email: string, password: string) {
    await axios.post(`/users`, {
        name,
        surname,
        email,
        password,
        position,
        phoneNumber,
        isActive,
        sendWelcomeEmail,
        role: 'USER'
    });
}

const CreateUser: React.FC = () => {
    const {t} = useTranslation();
    const [name, setName] = useState("");
    const [surname, setSurname] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordsMismatch, setPasswordsMismatch] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [position, setPosition] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [isActive, setIsActive] = useState<boolean>(true);
    const [sendWelcomeEmail, setSendWelcomeEmail] = useState<boolean>(true);

    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const isFormValid = Boolean(name && surname && email && password && confirmPassword);

    useEffect(() => {
        setPasswordsMismatch(!!password && !!confirmPassword && password !== confirmPassword);
    }, [password, confirmPassword]);

    const handleRegister = async () => {
        if (!isFormValid) return;
        {
            const error = validatePassword(password, t);
            if (error) {
                setPasswordError(error);
                return;
            }
        }
        if ((!!password) && password !== confirmPassword) {
            setPasswordsMismatch(true);
            return;
        }

        try {
            await createUser(name, surname, position, phoneNumber, isActive, sendWelcomeEmail, email, password);
            showSnackbar(t('createUser.userCreated'), "success");
            navigate("/admin/user/list");
        } catch (error) {
            showSnackbar(parseError(error), "error");
        }
    };

    return (
        <StyledContainer role="region" aria-labelledby="create-user-title">
            <PageHeader>
                <h2 id="create-user-title">{t('createUser.title')}</h2>
            </PageHeader>
            <CreateUserForm autoComplete="off">
                <FormGrid>
                    <StyledTextField
                        label={t('createUser.firstName')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        variant="outlined"
                        inputProps={{
                            'aria-label': String(t('createUser.firstName')),
                        }}
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('createUser.help.firstName')}
                                    ariaLabel={t('createUser.help.firstName') as string}
                                />
                            ),
                        }}
                    />
                    <StyledTextField
                        label={t('createUser.lastName')}
                        value={surname}
                        onChange={(e) => setSurname(e.target.value)}
                        fullWidth
                        variant="outlined"
                        inputProps={{
                            'aria-label': String(t('createUser.lastName')),
                        }}
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('createUser.help.lastName')}
                                    ariaLabel={t('createUser.help.lastName') as string}
                                />
                            ),
                        }}
                    />
                    <StyledTextField
                        label={t('createUser.email')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        variant="outlined"
                        inputProps={{
                            'aria-label': String(t('createUser.email')),
                        }}
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('createUser.help.email')}
                                    ariaLabel={t('createUser.help.email') as string}
                                />
                            ),
                        }}
                    />
                    <StyledFormControl fullWidth variant="outlined">
                        <InputLabel>{t('userEdit.statusLabel')}</InputLabel>
                        <Select
                            value={isActive.toString()}
                            onChange={(e) => setIsActive(e.target.value === "true")}
                            label={t('userEdit.statusLabel')}
                            sx={{
                                '& .MuiSelect-icon': {
                                    right: 40,
                                },
                            }}
                            input={
                                <OutlinedInput
                                    label={t('userEdit.statusLabel')}
                                    endAdornment={
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('createUser.help.status')}
                                            ariaLabel={t('createUser.help.status') as string}
                                        />
                                    }
                                    sx={{ pr: '72px' }}
                                />
                            }
                        >
                            <MenuItem value="true">{t('userEdit.statusOptions.active')}</MenuItem>
                            <MenuItem value="false">{t('userEdit.statusOptions.inactive')}</MenuItem>
                        </Select>
                    </StyledFormControl>
                    <StyledTextField
                        label={t('createUser.position')}
                        value={position}
                        onChange={(e) => setPosition(e.target.value)}
                        fullWidth
                        variant="outlined"
                        inputProps={{
                            'aria-label': String(t('createUser.position')),
                        }}
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('createUser.help.position')}
                                    ariaLabel={t('createUser.help.position') as string}
                                />
                            ),
                        }}
                    />
                    <StyledTextField
                        label={t('createUser.phoneNumber')}
                        type="tel"
                        inputProps={{
                            'aria-label': String(t('createUser.phoneNumber')),
                            inputMode: "numeric",
                            pattern: "^\\+?[0-9]*$",
                            maxLength: 15
                        }}
                        value={phoneNumber}
                        onChange={(e) => {
                            const cleaned = e.target.value.replace(/[^+\d]/g, "");
                            setPhoneNumber(cleaned);
                        }}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('createUser.help.phoneNumber')}
                                    ariaLabel={t('createUser.help.phoneNumber') as string}
                                />
                            ),
                        }}
                    />
                    <StyledTextField
                        label={t('createUser.password')}
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            setPasswordError("");
                        }}
                        onBlur={() => {
                            const error = validatePassword(password, t);
                            setPasswordError(error || "");
                        }}
                        fullWidth
                        variant="outlined"
                        error={!!passwordError}
                        helperText={passwordError}
                        inputProps={{
                            'aria-label': String(t('createUser.password')),
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" sx={{ gap: 0.5 }}>
                                    <IconButton
                                        aria-label={t('createUser.togglePasswordVisibility') as string || 'toggle password visibility'}
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff aria-hidden={true} focusable={false}/> : <Visibility aria-hidden={true} focusable={false}/>}
                                    </IconButton>
                                    <FieldHelpSuffix
                                        asAdornment
                                        title={t('createUser.help.password')}
                                        ariaLabel={t('createUser.help.password') as string}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <StyledTextField
                        label={t('createUser.repeatPassword')}
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        fullWidth
                        variant="outlined"
                        error={Boolean(passwordsMismatch)}
                        helperText={passwordsMismatch ? t('passwordValidation.match') : ""}
                        inputProps={{
                            'aria-label': String(t('createUser.repeatPassword')),
                        }}
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('createUser.help.repeatPassword')}
                                    ariaLabel={t('createUser.help.repeatPassword') as string}
                                />
                            ),
                        }}
                    />
                    <FullRow>
                        <FormControlLabel
                            control={<Checkbox checked={sendWelcomeEmail}
                                               onChange={(e) => setSendWelcomeEmail(e.target.checked)}/>}
                            label={
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    {t('createUser.sendWelcomeEmail')}
                                    <FieldHelpSuffix
                                        title={t('createUser.help.sendWelcomeEmail')}
                                        ariaLabel={t('createUser.help.sendWelcomeEmail') as string}
                                    />
                                </Box>
                            }
                            labelPlacement="start"
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                            }}
                        />
                    </FullRow>
                    <ActionsRow>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleRegister}
                            disabled={!isFormValid || !!passwordError || passwordsMismatch}
                            sx={{
                                padding: '10px 20px',
                                fontSize: '1rem',
                                width: {xs: '100%', sm: 'fit-content'}
                            }}
                        >
                            {t('createUser.createButton')}
                        </Button>
                    </ActionsRow>
                </FormGrid>
            </CreateUserForm>
        </StyledContainer>
    );
};

export default CreateUser;
